import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
    Autocomplete,
    Box,
    Button as MuiButton,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';
import { ResourceContextProvider, useNotify } from 'ra-core';
import { EmailRichTextInput } from '@/modules/quotes/EmailRichTextInput';
import {
    fetchEstimateEmailDraft,
    parseRecipientEmails,
    sendQuoteEstimateEmail,
} from '@/utils/quote_email_utils';

type FormValues = {
    body_html: string;
};

type SendEstimateEmailDialogProps = {
    open: boolean;
    onClose: () => void;
    quoteId: number;
    onSent?: () => void;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeEmail(value: string): string {
    return value.trim().toLowerCase();
}

function dedupeEmails(emails: string[]): string[] {
    const seen = new Set<string>();
    const unique: string[] = [];
    for (const email of emails) {
        const trimmed = email.trim();
        if (!trimmed) continue;
        const key = normalizeEmail(trimmed);
        if (seen.has(key)) continue;
        seen.add(key);
        unique.push(trimmed);
    }
    return unique;
}

/** Strip trailing comma/space separators the user typed while committing. */
function pendingToken(raw: string): string {
    return raw.replace(/[,\s]+$/g, '').trim();
}

export const SendEstimateEmailDialog = ({
    open,
    onClose,
    quoteId,
    onSent,
}: SendEstimateEmailDialogProps) => {
    const notify = useNotify();
    const form = useForm<FormValues>({ defaultValues: { body_html: '' } });
    const { reset, watch, getValues } = form;
    const bodyHtml = watch('body_html');

    const [recipients, setRecipients] = useState<string[]>([]);
    const [suggestionEmails, setSuggestionEmails] = useState<string[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [subject, setSubject] = useState('');
    const [toError, setToError] = useState('');
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!open || !quoteId) return;

        let cancelled = false;
        setLoading(true);
        setToError('');
        setInputValue('');

        fetchEstimateEmailDraft(quoteId)
            .then(draft => {
                if (cancelled) return;
                setRecipients(parseRecipientEmails(draft.to).emails);
                setSuggestionEmails(
                    dedupeEmails(
                        draft.related_people.map(p => p.email.trim()).filter(Boolean)
                    )
                );
                setSubject(draft.subject);
                reset({ body_html: draft.body_html });
            })
            .catch(e => {
                if (cancelled) return;
                notify(e instanceof Error ? e.message : 'Failed to load email draft', {
                    type: 'error',
                });
                onClose();
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, quoteId, reset, notify, onClose]);

    const validateRecipients = (emails: string[]) => {
        const result = parseRecipientEmails(emails.join(', '));
        if (!result.valid) {
            setToError(result.error || 'Invalid email');
            return false;
        }
        setToError('');
        return true;
    };

    /** Commit typed text into a chip. Returns updated list, or null if invalid pending text. */
    const commitPending = (
        current: string[],
        rawInput: string
    ): { emails: string[]; error?: string } => {
        const token = pendingToken(rawInput);
        if (!token) {
            return { emails: current };
        }
        if (!EMAIL_RE.test(token)) {
            return {
                emails: current,
                error: `Invalid email address: ${token}`,
            };
        }
        return { emails: dedupeEmails([...current, token]) };
    };

    const handleSend = async () => {
        const committed = commitPending(recipients, inputValue);
        if (committed.error) {
            setToError(committed.error);
            return;
        }
        setRecipients(committed.emails);
        setInputValue('');

        if (!validateRecipients(committed.emails)) return;

        const subjectTrimmed = subject.trim();
        if (!subjectTrimmed) {
            notify('Subject is required', { type: 'error' });
            return;
        }

        const normalizedBody = (getValues('body_html') || '').trim();
        if (!normalizedBody) {
            notify('Email body is required', { type: 'error' });
            return;
        }

        setSending(true);
        try {
            const result = await sendQuoteEstimateEmail(quoteId, {
                to: committed.emails.join(', '),
                subject: subjectTrimmed,
                body_html: normalizedBody,
            });
            notify(`Estimate sent to ${result.recipient}`, { type: 'success' });
            onSent?.();
            onClose();
        } catch (e) {
            notify(e instanceof Error ? e.message : 'Failed to send estimate email', {
                type: 'error',
            });
        } finally {
            setSending(false);
        }
    };

    const recipientKeys = new Set(recipients.map(normalizeEmail));
    const autocompleteOptions = suggestionEmails.filter(
        email => !recipientKeys.has(normalizeEmail(email))
    );

    const effectiveEmails = (() => {
        const token = pendingToken(inputValue);
        if (!token) return recipients;
        if (!EMAIL_RE.test(token)) return recipients;
        return dedupeEmails([...recipients, token]);
    })();
    const toValidation = parseRecipientEmails(effectiveEmails.join(', '));
    const canSend =
        !loading &&
        !sending &&
        toValidation.valid &&
        !!subject.trim() &&
        !!(bodyHtml || '').trim();

    return (
        <Dialog
            open={open}
            onClose={() => !sending && onClose()}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Send estimate to client</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box display="flex" flexDirection="column" gap={2} pt={1}>
                        <Autocomplete
                            multiple
                            freeSolo
                            options={autocompleteOptions}
                            value={recipients}
                            inputValue={inputValue}
                            onInputChange={(_, value, reason) => {
                                // Keep controlled; ignore Autocomplete clearing after our commit
                                if (reason === 'reset') return;
                                setInputValue(value);
                            }}
                            onChange={(_, value) => {
                                const next = dedupeEmails(
                                    value.map(v =>
                                        typeof v === 'string' ? v.trim() : String(v)
                                    )
                                );
                                setRecipients(next);
                                if (toError) validateRecipients(next);
                            }}
                            renderTags={(value, getTagProps) =>
                                value.map((email, index) => {
                                    const { key, ...tagProps } = getTagProps({ index });
                                    return (
                                        <Chip
                                            key={key}
                                            label={email}
                                            size="small"
                                            {...tagProps}
                                        />
                                    );
                                })
                            }
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label="To"
                                    error={!!toError}
                                    helperText={
                                        toError ||
                                        'Contractor and client emails are prefilled when available. Related people appear as suggestions. Press space or comma to add; Send includes typed email.'
                                    }
                                    onBlur={() => {
                                        const committed = commitPending(recipients, inputValue);
                                        if (committed.error) {
                                            setToError(committed.error);
                                            return;
                                        }
                                        setRecipients(committed.emails);
                                        setInputValue('');
                                        validateRecipients(committed.emails);
                                    }}
                                    onKeyDown={e => {
                                        if (
                                            e.key !== 'Enter' &&
                                            e.key !== ',' &&
                                            e.key !== ' '
                                        ) {
                                            return;
                                        }
                                        e.preventDefault();
                                        e.stopPropagation();

                                        const committed = commitPending(recipients, inputValue);
                                        if (committed.error) {
                                            setToError(committed.error);
                                            return;
                                        }
                                        if (!pendingToken(inputValue)) return;
                                        setRecipients(committed.emails);
                                        setInputValue('');
                                        setToError('');
                                    }}
                                />
                            )}
                        />
                        <TextField
                            label="Subject"
                            value={subject}
                            onChange={e => setSubject(e.target.value)}
                            fullWidth
                        />
                        <FormProvider {...form}>
                            <ResourceContextProvider value="quotes">
                                <EmailRichTextInput
                                    source="body_html"
                                    label="Email content"
                                    fullWidth
                                />
                            </ResourceContextProvider>
                        </FormProvider>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <MuiButton onClick={onClose} disabled={sending}>
                    Cancel
                </MuiButton>
                <MuiButton
                    variant="contained"
                    onClick={handleSend}
                    disabled={!canSend}
                    autoFocus
                >
                    {sending ? 'Sending…' : 'Send'}
                </MuiButton>
            </DialogActions>
        </Dialog>
    );
};
